// A doc board's page, as agents read and write it: Markdown at the edge, the editor's own document
// (Tiptap JSON) inside. The browser keeps a page as the editor's JSON, so nothing a person writes goes
// through Markdown and back; an agent reads a page as Markdown (toMarkdown), writes Markdown
// (fromMarkdown) and the server puts the blocks it makes where it asked (editBody). Pasting Markdown in
// the browser uses fromMarkdown too.
//
// The blocks, exactly: paragraph, heading (1 to 3), bulleted, numbered and to-do lists (nested), toggle
// (details), quote, code, divider, table, a widget block and a picture (![caption](address), the address
// the board's own: a picture is kept by the board, never loaded from where it came from). The marks: bold,
// italic, code, strike and link (a link to another page is a link whose href is page:<id>).

import { Lexer, type Token, type Tokens } from "marked";
import { LISTS, asBody, keptKey, newBlockId, plainText, widgetIds, withIds, type DocBody, type DocMark, type DocNode } from "./docblocks";
import { pictureKeyOf, picturePath } from "./imageInfo";
import { textColor } from "./textColors";

export * from "./docblocks";

// ---------------------------------------------------------------------------
// the body as Markdown

export interface ToMdContext {
  /** a page's title, or null when it is not there any more */
  pageTitle?(id: string): string | null;
  /** a widget's title, or null when it is not there any more */
  widgetTitle?(id: string): string | null;
  /** a kept picture's full address (the board's origin and its path); its path alone without this */
  pictureAddress?(key: string): string;
}

// a colour outermost: <span data-color="orange">**bold** words</span> (Features 1, 20 Oct)
const MARK_ORDER = ["color", "link", "bold", "italic", "strike"];
const DELIM: Record<string, string> = { bold: "**", italic: "*", strike: "~~" };

function escapeText(s: string, table: boolean): string {
  let t = s
    .replace(/\\/g, "\\\\")
    .replace(/([*`[\]~])/g, "\\$1")
    .replace(/(^|[^A-Za-z0-9])_|_(?=$|[^A-Za-z0-9])/g, (m) => m.replace("_", "\\_"))
    .replace(/<(?=[A-Za-z/!?])/g, "\\<")
    .replace(/&(?=#?[A-Za-z0-9]+;)/g, "\\&");
  if (table) t = t.replace(/\|/g, "\\|");
  return t;
}

/** What a line starting with these words would turn into, made plain words again. */
function escapeLineStarts(s: string): string {
  return s
    .split("\n")
    .map((line) => line.replace(/^(\s*)([#>+=|-]|\d+[.)](?=\s|$))/, (_m, sp: string, c: string) => sp + (c.length > 1 ? c.replace(/([.)])$/, "\\$1") : "\\" + c)))
    .join("\n");
}

function codeSpan(text: string): string {
  const runs = text.match(/`+/g) ?? [];
  const n = Math.max(0, ...runs.map((r) => r.length)) + 1;
  const tick = "`".repeat(n);
  const pad = text.startsWith("`") || text.endsWith("`") || (text.startsWith(" ") && text.endsWith(" ") && text.trim()) ? " " : "";
  return tick + pad + text + pad + tick;
}

const sameMark = (a: DocMark, b: DocMark) => a.type === b.type && (a.type !== "link" || a.attrs?.href === b.attrs?.href) && (a.type !== "color" || a.attrs?.color === b.attrs?.color);

/** A run of text with its marks, as Markdown. */
export function inlineMarkdown(nodes: DocNode[] | undefined, ctx: ToMdContext = {}, table = false): string {
  if (!nodes?.length) return "";
  // what is being written, a buffer for each link open (its words), the outermost first
  const bufs: { text: string; words: string; mark: DocMark | null }[] = [{ text: "", words: "", mark: null }];
  const open: DocMark[] = [];
  const top = () => bufs[bufs.length - 1];
  const write = (s: string) => (top().text += s);
  const closeTo = (keep: number) => {
    while (open.length > keep) {
      const m = open.pop()!;
      // a mark never closes after a space: the space goes after it
      const trail = /\s+$/.exec(top().text)?.[0] ?? "";
      if (trail) top().text = top().text.slice(0, -trail.length);
      if (m.type === "link") {
        const b = bufs.pop()!;
        const href = String(m.attrs?.href ?? "");
        if (href.startsWith("page:")) {
          const title = ctx.pageTitle?.(href.slice(5)) ?? null;
          // a link that reads as its page's title is the page's name; another keeps its words
          write(title && title === b.words && !/[\]\[\n]/.test(title) ? `[[${title}]]` : `[${b.text}](${href})`);
        } else write(`[${b.text}](${href.replace(/[()\s]/g, (c) => encodeURIComponent(c))})`);
      } else if (m.type === "color") write("</span>");
      else write(DELIM[m.type] ?? "");
      write(trail);
    }
  };
  for (const node of nodes) {
    const marks = (node.marks ?? []).filter((m) => m.type !== "code" && (m.type === "link" || DELIM[m.type] || (m.type === "color" && textColor(m.attrs?.color)))).sort((a, b) => MARK_ORDER.indexOf(a.type) - MARK_ORDER.indexOf(b.type));
    let keep = 0;
    while (keep < open.length && keep < marks.length && sameMark(marks[keep], open[keep])) keep++;
    closeTo(keep);
    if (node.type === "hardBreak") {
      write(table ? "<br>" : "\n");
      continue;
    }
    if (node.type !== "text") continue;
    let text = node.text ?? "";
    for (const m of marks.slice(open.length)) {
      // nor opens before one
      const lead = /^\s+/.exec(text)?.[0] ?? "";
      if (lead) {
        write(escapeText(lead, table));
        text = text.slice(lead.length);
      }
      if (m.type === "link") bufs.push({ text: "", words: "", mark: m });
      else if (m.type === "color") write(`<span data-color="${textColor(m.attrs?.color)}">`);
      else write(DELIM[m.type] ?? "");
      open.push(m);
    }
    const code = (node.marks ?? []).some((m) => m.type === "code");
    for (const b of bufs) if (b.mark) b.words += text;
    write(code ? codeSpan(text) : escapeText(text, table));
  }
  closeTo(0);
  return bufs[0].text;
}

function indent(s: string, by: string): string {
  return s
    .split("\n")
    .map((l, i) => (i === 0 || !l ? l : by + l))
    .join("\n");
}

function listItemMd(item: DocNode, marker: string, ctx: ToMdContext, pad = " ".repeat(marker.length)): string {
  const [first, ...rest] = item.content ?? [];
  const head = first?.type === "paragraph" ? escapeLineStarts(inlineMarkdown(first.content, ctx)) : first ? blockMarkdown(first, ctx) : "";
  const tail = rest.map((b) => blockMarkdown(b, ctx));
  return marker + indent([head, ...tail].join("\n"), pad);
}

function tableMd(node: DocNode, ctx: ToMdContext): string {
  const rows = (node.content ?? []).map((r) => (r.content ?? []).map((c) => (c.content ?? []).map((p) => (p.type === "paragraph" || p.type === "heading" ? inlineMarkdown(p.content, ctx, true) : plainText(p))).join("<br>") || " "));
  if (!rows.length) return "";
  const width = Math.max(...rows.map((r) => r.length));
  const line = (r: string[]) => "| " + Array.from({ length: width }, (_, i) => r[i] ?? " ").join(" | ") + " |";
  return [line(rows[0]), "|" + Array.from({ length: width }, () => " --- ").join("|") + "|", ...rows.slice(1).map(line)].join("\n");
}

/** One block as Markdown. */
export function blockMarkdown(node: DocNode, ctx: ToMdContext = {}): string {
  switch (node.type) {
    case "paragraph":
      return escapeLineStarts(inlineMarkdown(node.content, ctx));
    case "heading": {
      const level = Math.min(3, Math.max(1, Number(node.attrs?.level) || 1));
      return "#".repeat(level) + " " + inlineMarkdown(node.content, ctx).replace(/\n/g, " ");
    }
    case "bulletList":
      return (node.content ?? []).map((it) => listItemMd(it, "- ", ctx)).join("\n");
    case "orderedList": {
      const start = Number(node.attrs?.start) || 1;
      return (node.content ?? []).map((it, i) => listItemMd(it, `${start + i}. `, ctx)).join("\n");
    }
    case "taskList":
      // what sits inside a to-do is indented as far as its "- ", or Markdown reads it as code
      return (node.content ?? []).map((it) => listItemMd(it, it.attrs?.checked ? "- [x] " : "- [ ] ", ctx, "  ")).join("\n");
    case "blockquote":
      return (node.content ?? [])
        .map((b) => blockMarkdown(b, ctx))
        .join("\n\n")
        .split("\n")
        .map((l) => (l ? "> " + l : ">"))
        .join("\n");
    case "codeBlock": {
      const text = plainCode(node);
      const runs = text.match(/`{3,}/g) ?? [];
      const fence = "`".repeat(Math.max(3, ...runs.map((r) => r.length + 1)));
      const lang = typeof node.attrs?.language === "string" ? node.attrs.language.replace(/[^\w+#.-]/g, "") : "";
      return `${fence}${lang}\n${text}\n${fence}`;
    }
    case "horizontalRule":
      return "---";
    case "details": {
      const summary = (node.content ?? []).find((c) => c.type === "detailsSummary");
      const inside = (node.content ?? []).find((c) => c.type === "detailsContent");
      const title = inlineMarkdown(summary?.content, ctx).replace(/\n/g, " ");
      const body = (inside?.content ?? []).map((b) => blockMarkdown(b, ctx)).filter(Boolean).join("\n\n");
      return `<details><summary>${title}</summary>\n\n${body}\n\n</details>`;
    }
    case "table":
      return tableMd(node, ctx);
    case "widget": {
      const id = String(node.attrs?.item ?? "");
      const title = ctx.widgetTitle ? ctx.widgetTitle(id) : null;
      return `[widget: ${(title ?? (ctx.widgetTitle ? "removed" : "widget")).replace(/[[\]]/g, "")}](widget:${id})`;
    }
    case "image": {
      // a picture the board does not keep yet has no address to give
      const key = keptKey(node);
      if (!key) return "";
      const caption = plainText(node).replace(/\s+/g, " ").replace(/([\\[\]])/g, "\\$1");
      const address = (ctx.pictureAddress?.(key) ?? picturePath(key)).replace(/[()\s]/g, (c) => encodeURIComponent(c));
      return `![${caption}](${address})`;
    }
    default:
      return node.content ? (node.content.every((c) => c.type === "text" || c.type === "hardBreak") ? escapeLineStarts(inlineMarkdown(node.content, ctx)) : node.content.map((b) => blockMarkdown(b, ctx)).join("\n\n")) : "";
  }
}

function plainCode(node: DocNode): string {
  return (node.content ?? []).map((c) => (c.type === "hardBreak" ? "\n" : (c.text ?? ""))).join("");
}

/** A page's body as Markdown, a blank line between its blocks. */
export function toMarkdown(body: unknown, ctx: ToMdContext = {}): string {
  return asBody(body)
    .content.map((b) => blockMarkdown(b, ctx))
    .filter(Boolean)
    .join("\n\n");
}

// ---------------------------------------------------------------------------
// Markdown as blocks

export interface FromMdContext {
  /** the page a [[Title]] names: its id, or null when there is none */
  findPage?(title: string): string | null;
  /** whether a page by this id is there */
  hasPage?(id: string): boolean;
  /** a page's title, so a link by its name reads as the page is called */
  pageTitle?(id: string): string | null;
  /** whether a widget by this id is on the board, to place */
  hasWidget?(id: string): boolean;
  /**
   * The picture an image's address names, as the board keeps it (its key and size), or why it could not be
   * kept. Without it, the board's own picture addresses are the pictures they name and any other web address
   * is a picture still to be fetched (its link, for the editor to ask the board to keep).
   */
  picture?(href: string): { key: string; w: number; h: number } | { error: string } | null;
  /** the board's own origin, to know its picture addresses written in full */
  origin?: string | null;
}

export interface FromMdResult {
  blocks: DocNode[];
  /** [[Title]] links that named no page, left as words */
  unresolved: string[];
  /** widgets the Markdown placed, by id */
  widgets: string[];
  /** widget:… ids that are not on the board, left as words */
  missingWidgets: string[];
  /** the kept pictures the Markdown placed, by key */
  pictures: string[];
  /** pictures the board could not keep, left as links, and why */
  failedPictures: { what: string; why: string }[];
}

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", "#39": "'" };
function decode(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|[A-Za-z]+[0-9]*);/g, (m, e: string) => {
    if (e[0] === "#") {
      const n = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : m;
    }
    return ENTITIES[e] ?? m;
  });
}

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

class Reader {
  unresolved = new Set<string>();
  widgets: string[] = [];
  missing = new Set<string>();
  pictures: string[] = [];
  failed: { what: string; why: string }[] = [];
  constructor(private ctx: FromMdContext) {}

  /** An image as a picture block of its own, or null to leave it as words (it could not be kept, or is no web address). */
  picture(t: Tokens.Image): DocNode | null {
    const href = t.href.trim();
    const caption = decode(t.text ?? "")
      .replace(/\\([\\[\]])/g, "$1")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 500);
    const content = caption ? [textNode(caption, [])] : undefined;
    const block = (key: string | null, w: number | null, h: number | null, link: string | null = null): DocNode => ({ type: "image", attrs: { key, link, w: w || null, h: h || null, width: "full" }, ...(content ? { content } : {}) });
    if (this.ctx.picture) {
      const p = this.ctx.picture(href);
      if (p && "key" in p) {
        this.pictures.push(p.key);
        return block(p.key, p.w, p.h);
      }
      this.failed.push({ what: href.length > 160 ? href.slice(0, 157) + "..." : href, why: p && "error" in p ? p.error : "it could not be kept" });
      return null;
    }
    const own = pictureKeyOf(href, this.ctx.origin);
    if (own) return block(own, null, null);
    return /^https?:\/\//i.test(href) ? block(null, null, null, href) : null;
  }

  /**
   * A paragraph's words, with each picture in it a block of its own where it stood (a picture is a block,
   * never inside a line). A paragraph with no picture is read as it always was.
   */
  paragraph(tokens: Token[] | undefined, words: () => DocNode[]): DocNode[] {
    if (!(tokens ?? []).some((t) => t.type === "image")) return [this.widgetIn(tokens) ?? { type: "paragraph", content: words() }];
    const out: DocNode[] = [];
    let run: Token[] = [];
    const flush = () => {
      const widget = this.widgetIn(run);
      const words = widget ? [] : trimEnds(this.inline(run));
      if (widget || words.length) out.push(widget ?? { type: "paragraph", content: words });
      run = [];
    };
    for (const t of tokens ?? []) {
      const pic = t.type === "image" ? this.picture(t as Tokens.Image) : null;
      if (pic) {
        flush();
        out.push(pic);
      } else run.push(t);
    }
    flush();
    return out.length ? out : [{ type: "paragraph" }];
  }

  text(s: string, marks: DocMark[]): DocNode[] {
    if (!s) return [];
    // [[Page title]] links a page by its name
    const out: DocNode[] = [];
    let last = 0;
    for (const m of s.matchAll(/\[\[([^\][\n]{1,200})\]\]/g)) {
      const title = m[1].trim();
      const id = title ? (this.ctx.findPage?.(title) ?? null) : null;
      if (!id) {
        if (title) this.unresolved.add(title);
        continue;
      }
      if (m.index! > last) out.push(textNode(s.slice(last, m.index), marks));
      out.push(textNode(this.ctx.pageTitle?.(id) || title, [...marks.filter((x) => x.type !== "link"), { type: "link", attrs: { href: `page:${id}` } }]));
      last = m.index! + m[0].length;
    }
    if (last < s.length) out.push(textNode(s.slice(last), marks));
    return out;
  }

  inline(tokens: Token[] | undefined, outer: DocMark[] = []): DocNode[] {
    const out: DocNode[] = [];
    // neighbouring words are read as one, so a [[Title]] the lexer split still links
    let pending = "";
    // a named colour opened by <span data-color="…"> and closed by </span>, at this level (Features 1, 20 Oct);
    // a span of any other kind is words, as before
    const colors: (string | null)[] = [];
    let marks = outer;
    const recolor = () => {
      const c = [...colors].reverse().find((x) => x !== null) ?? null;
      marks = c ? [...outer.filter((m) => m.type !== "color"), { type: "color", attrs: { color: c } }] : outer;
    };
    const flush = () => {
      if (pending) out.push(...this.text(pending, marks));
      pending = "";
    };
    for (const t of tokens ?? []) {
      switch (t.type) {
        case "text": {
          const tt = t as Tokens.Text;
          if (tt.tokens?.length && tt.tokens.some((x) => x.type !== "text")) {
            flush();
            out.push(...this.inline(tt.tokens, marks));
          } else pending += decode(tt.text);
          break;
        }
        case "escape":
          pending += (t as Tokens.Escape).text;
          break;
        case "strong":
        case "em":
        case "del": {
          flush();
          const type = t.type === "strong" ? "bold" : t.type === "em" ? "italic" : "strike";
          out.push(...this.inline((t as Tokens.Strong).tokens, marks.some((m) => m.type === type) ? marks : [...marks, { type }]));
          break;
        }
        case "codespan":
          flush();
          out.push(textNode((t as Tokens.Codespan).text, [...marks.filter((m) => m.type === "link"), { type: "code" }]));
          break;
        case "br":
          flush();
          out.push({ type: "hardBreak" });
          break;
        case "link": {
          flush();
          const l = t as Tokens.Link;
          const href = l.href.trim();
          if (href.startsWith("page:")) {
            const id = href.slice(5);
            if (this.ctx.hasPage && !this.ctx.hasPage(id)) {
              this.unresolved.add(id);
              out.push(...this.inline(l.tokens, marks));
            } else out.push(...this.inline(l.tokens, [...marks.filter((m) => m.type !== "link"), { type: "link", attrs: { href: `page:${id}` } }]));
          } else if (SAFE_HREF.test(href)) out.push(...this.inline(l.tokens, [...marks.filter((m) => m.type !== "link"), { type: "link", attrs: { href } }]));
          else out.push(...this.inline(l.tokens, marks));
          break;
        }
        case "image": {
          flush();
          const im = t as Tokens.Image;
          // a picture where a block cannot go (a table's cell, a heading), or one the board could not keep, is its words, linked
          if (SAFE_HREF.test(im.href)) out.push(textNode(im.text || im.href, [...marks, { type: "link", attrs: { href: im.href } }]));
          else if (im.text) out.push(textNode(im.text, marks));
          break;
        }
        case "html": {
          const raw = (t as Tokens.HTML).text;
          const open = /^<span\s+data-colou?r\s*=\s*["']?([A-Za-z]+)["']?\s*>$/i.exec(raw.trim());
          if (/^<br\s*\/?>$/i.test(raw.trim())) {
            flush();
            out.push({ type: "hardBreak" });
          } else if (open && textColor(open[1])) {
            flush();
            colors.push(textColor(open[1]));
            recolor();
          } else if (/^<span\b[^>]*>$/i.test(raw.trim())) {
            // another span is its words, as before, and its </span> closes it, never a colour
            colors.push(null);
            pending += raw;
          } else if (/^<\/span\s*>$/i.test(raw.trim()) && colors.length) {
            if (colors.pop() === null) pending += raw;
            else {
              flush();
              recolor();
            }
          } else pending += raw;
          break;
        }
        default:
          if ("text" in t && typeof t.text === "string") pending += decode(t.text);
      }
    }
    flush();
    return mergeText(out);
  }

  /** A paragraph that is only a widget's link places that widget. */
  widgetIn(tokens: Token[] | undefined): DocNode | null {
    const real = (tokens ?? []).filter((t) => !(t.type === "text" && !(t as Tokens.Text).text.trim()));
    if (real.length !== 1 || real[0].type !== "link") return null;
    const href = (real[0] as Tokens.Link).href.trim();
    if (!href.startsWith("widget:")) return null;
    const id = href.slice(7);
    if (!/^[A-Za-z0-9_-]{1,60}$/.test(id) || (this.ctx.hasWidget && !this.ctx.hasWidget(id))) {
      this.missing.add(id);
      return { type: "paragraph", content: [textNode((real[0] as Tokens.Link).text || id, [])] };
    }
    this.widgets.push(id);
    return { type: "widget", attrs: { item: id } };
  }

  listItems(items: Tokens.ListItem[], ordered: boolean, start: number): DocNode[] {
    // to-dos and other items in one list become a to-do list and a list, side by side
    const runs: { task: boolean; items: Tokens.ListItem[] }[] = [];
    for (const it of items) {
      const task = !ordered && !!it.task;
      const last = runs[runs.length - 1];
      if (last && last.task === task) last.items.push(it);
      else runs.push({ task, items: [it] });
    }
    let n = start;
    return runs.map((run) => {
      const content = run.items.map((it) => {
        const inner = this.blocks((it.tokens ?? []).filter((x) => x.type !== "checkbox"));
        const body = inner[0]?.type === "paragraph" ? inner : [{ type: "paragraph" } as DocNode, ...inner];
        return run.task ? { type: "taskItem", attrs: { checked: !!it.checked }, content: body } : { type: "listItem", content: body };
      });
      const node: DocNode = run.task ? { type: "taskList", content } : ordered ? { type: "orderedList", attrs: { start: n }, content } : { type: "bulletList", content };
      n += run.items.length;
      return node;
    });
  }

  blocks(tokens: Token[]): DocNode[] {
    const out: DocNode[] = [];
    // an open toggle collects what follows it until </details>
    const stack: { open: boolean; summary: DocNode[]; children: DocNode[] }[] = [];
    const push = (n: DocNode) => (stack.length ? stack[stack.length - 1].children : out).push(n);
    const close = () => {
      const f = stack.pop()!;
      push({ type: "details", content: [{ type: "detailsSummary", content: f.summary }, { type: "detailsContent", content: f.children.length ? f.children : [{ type: "paragraph" }] }] });
    };
    for (const t of tokens) {
      switch (t.type) {
        case "space":
        case "def":
          break;
        case "heading": {
          const h = t as Tokens.Heading;
          push({ type: "heading", attrs: { level: Math.min(3, h.depth) }, content: this.inline(h.tokens) });
          break;
        }
        case "paragraph": {
          const p = t as Tokens.Paragraph;
          for (const n of this.paragraph(p.tokens, () => this.inline(p.tokens))) push(n);
          break;
        }
        case "text": {
          // a tight list item's words
          const tt = t as Tokens.Text;
          for (const n of this.paragraph(tt.tokens, () => (tt.tokens ? this.inline(tt.tokens) : this.text(decode(tt.text), [])))) push(n);
          break;
        }
        case "list": {
          const l = t as Tokens.List;
          for (const n of this.listItems(l.items, l.ordered, typeof l.start === "number" ? l.start : 1)) push(n);
          break;
        }
        case "blockquote": {
          const inner = this.blocks((t as Tokens.Blockquote).tokens);
          push({ type: "blockquote", content: inner.length ? inner : [{ type: "paragraph" }] });
          break;
        }
        case "code": {
          const c = t as Tokens.Code;
          const lang = (c.lang ?? "").trim().split(/\s/)[0].slice(0, 30);
          push({ type: "codeBlock", attrs: { language: lang || null }, ...(c.text ? { content: [{ type: "text", text: c.text }] } : {}) });
          break;
        }
        case "hr":
          push({ type: "horizontalRule" });
          break;
        case "table": {
          const tb = t as Tokens.Table;
          const cell = (type: string, c: Tokens.TableCell): DocNode => ({ type, attrs: { colspan: 1, rowspan: 1, colwidth: null }, content: [{ type: "paragraph", content: this.inline(c.tokens) }] });
          push({ type: "table", content: [{ type: "tableRow", content: tb.header.map((c) => cell("tableHeader", c)) }, ...tb.rows.map((r) => ({ type: "tableRow", content: r.map((c) => cell("tableCell", c)) }))] });
          break;
        }
        case "html": {
          // a toggle is <details><summary>Title</summary> … </details>; any other HTML is words
          const raw = (t as Tokens.HTML).text;
          const re = /<details(\s+open)?\s*>|<\/details\s*>|<summary\s*>([\s\S]*?)<\/summary\s*>/gi;
          let last = 0;
          let any = false;
          const between = (s: string) => {
            if (!s.trim()) return;
            for (const n of this.blocks(lexer().lex(s))) push(n);
          };
          for (const m of raw.matchAll(re)) {
            any = true;
            between(raw.slice(last, m.index));
            last = m.index! + m[0].length;
            if (m[0].toLowerCase().startsWith("<details")) stack.push({ open: !!m[1], summary: [], children: [] });
            else if (m[0].toLowerCase().startsWith("</details")) {
              if (stack.length) close();
            } else if (stack.length) stack[stack.length - 1].summary = this.inline(Lexer.lexInline(m[2].trim(), { gfm: true }));
          }
          if (any) between(raw.slice(last));
          else push({ type: "paragraph", content: this.text(raw.trim(), []) });
          break;
        }
        default:
          if ("text" in t && typeof t.text === "string" && t.text.trim()) push({ type: "paragraph", content: this.text(decode(t.text), []) });
      }
    }
    while (stack.length) close();
    return out;
  }
}

function textNode(text: string, marks: DocMark[]): DocNode {
  return marks.length ? { type: "text", text, marks } : { type: "text", text };
}

/** A line's words without the spaces and breaks at its two ends (where a picture stood beside them). */
function trimEnds(nodes: DocNode[]): DocNode[] {
  const out = [...nodes];
  while (out.length && out[0].type === "hardBreak") out.shift();
  while (out.length && out[out.length - 1].type === "hardBreak") out.pop();
  if (out[0]?.type === "text") out[0] = { ...out[0], text: (out[0].text ?? "").replace(/^\s+/, "") };
  const last = out.length - 1;
  if (out[last]?.type === "text") out[last] = { ...out[last], text: (out[last].text ?? "").replace(/\s+$/, "") };
  return out.filter((n) => n.type !== "text" || n.text);
}

/** Neighbouring words with the same marks, as one. */
function mergeText(nodes: DocNode[]): DocNode[] {
  const out: DocNode[] = [];
  for (const n of nodes) {
    if (n.type === "text" && !n.text) continue;
    const prev = out[out.length - 1];
    if (prev && prev.type === "text" && n.type === "text" && JSON.stringify(prev.marks ?? []) === JSON.stringify(n.marks ?? [])) prev.text += n.text ?? "";
    else out.push({ ...n });
  }
  return out;
}

const lexer = () => new Lexer({ gfm: true, breaks: true });

/** Markdown as the editor's blocks, each with its id. */
export function fromMarkdown(md: string, ctx: FromMdContext = {}): FromMdResult {
  const r = new Reader(ctx);
  const blocks = r.blocks(lexer().lex(md.replace(/\r\n?/g, "\n"))).map((b) => withIds(b, true));
  return { blocks, unresolved: [...r.unresolved], widgets: r.widgets, missingWidgets: [...r.missing], pictures: r.pictures, failedPictures: r.failed };
}

// ---------------------------------------------------------------------------
// writing to a page: where the new blocks go

export type WriteMode = "append" | "replace_all" | "insert_after" | "replace";
export const WRITE_MODES: WriteMode[] = ["append", "replace_all", "insert_after", "replace"];

/**
 * A page read as the places an anchor can name: each block, and each item of a list on its own (its own
 * list inside it with it), so a to-do can be ticked or a line put after it without the rest of its list.
 */
function units(blocks: DocNode[]): DocNode[] {
  const out: DocNode[] = [];
  for (const b of blocks) {
    if (LISTS.has(b.type) && (b.content?.length ?? 0) > 1) {
      b.content!.forEach((item, i) => {
        const attrs = { ...(b.attrs ?? {}) };
        if (i > 0) attrs.id = newBlockId();
        if (b.type === "orderedList") attrs.start = (Number(b.attrs?.start) || 1) + i;
        out.push({ type: b.type, attrs, content: [item] });
      });
    } else out.push(b);
  }
  return out;
}

/** How many list items sit nested inside a block's own list items, at any depth. */
function nestedLines(b: DocNode): number {
  if (!LISTS.has(b.type)) return 0;
  let n = 0;
  const walk = (node: DocNode, inside: boolean) => {
    if (inside && (node.type === "listItem" || node.type === "taskItem")) n++;
    node.content?.forEach((c) => walk(c, inside || LISTS.has(c.type)));
  };
  for (const item of b.content ?? []) item.content?.forEach((c) => walk(c, LISTS.has(c.type)));
  return n;
}

/** Lists that sit next to each other, of one kind, as one list again. */
function joinLists(blocks: DocNode[]): DocNode[] {
  const out: DocNode[] = [];
  for (const b of blocks) {
    const prev = out[out.length - 1];
    if (prev && LISTS.has(b.type) && prev.type === b.type) out[out.length - 1] = { ...prev, content: [...(prev.content ?? []), ...(b.content ?? [])] };
    else out.push(b);
  }
  return out;
}

const squash = (s: string) => s.replace(/\s+/g, " ").trim();

export interface AnchorMatch {
  from: number;
  to: number;
}

/**
 * Where an anchor is: a short exact text in one block (or one list item), or start...end for a run of
 * them. One that matches no place or more than one says so, and nothing is changed.
 */
export function findAnchor(blocks: DocNode[], anchor: string, ctx: ToMdContext = {}): AnchorMatch | { error: string } {
  const list = units(blocks);
  const texts = list.map((u) => [squash(plainText(u)), squash(blockMarkdown(u, ctx))]);
  const hits = (a: string) => {
    const q = squash(a);
    const out: number[] = [];
    if (!q) return out;
    texts.forEach(([t, m], i) => {
      if (t.includes(q) || m.includes(q)) out.push(i);
    });
    return out;
  };
  const say = (a: string, n: number) =>
    n === 0 ? `No block on the page has the words "${a}". read_page shows the page as it is now.` : `"${a}" is in ${n} places on the page. Use more of the words, so it names one.`;
  const whole = hits(anchor);
  if (whole.length === 1) return { from: whole[0], to: whole[0] };
  const cut = /\.\.\.|…/.exec(anchor);
  if (!cut || whole.length > 1) return { error: say(anchor, whole.length) };
  const a = anchor.slice(0, cut.index).trim();
  const b = anchor.slice(cut.index + cut[0].length).trim();
  if (!a || !b) return { error: `An anchor that names a run of blocks is "start...end", with words from its first block and its last.` };
  const starts = hits(a);
  if (starts.length !== 1) return { error: say(a, starts.length) };
  const ends = hits(b).filter((i) => i >= starts[0]);
  if (ends.length !== 1) return { error: ends.length ? say(b, ends.length) : `No block from "${a}" on has the words "${b}".` };
  return { from: starts[0], to: ends[0] };
}

export interface EditResult {
  body: DocBody;
  /** the ids of the blocks that are new or changed, for the page to glow them */
  changed: string[];
  /** widgets whose blocks went, and so go with them */
  removedWidgets: string[];
  /** an append of what the page already ends with: nothing was added again (Fixes 10) */
  already?: boolean;
}

/**
 * An anchor inside one toggle, list item or table names a block in there, not the whole of it (Fixes 10): an agent
 * that changed one line of a toggle took out the toggle, its title and its other lines, and a line put after one
 * inside went below it. So the edit is made inside, and what holds it stays as it was. An anchor in the toggle's
 * title or the item's own line still names the whole, as does a write that brings the toggle or the item's own line
 * back with it. A table takes rows written as | a | b | lines among its own.
 * null: the whole block, as before.
 */
function editInside(unit: DocNode, blocks: DocNode[], mode: WriteMode, anchor: string, ctx: ToMdContext): { node: DocNode; changed: string[] } | { error: string } | null {
  const parts = anchor.split(/\.\.\.|…/).map(squash).filter(Boolean);
  const names = (n: DocNode | undefined) => !!n && parts.some((q) => squash(plainText(n)).includes(q) || squash(blockMarkdown(n, ctx)).includes(q));
  // its own words name the whole: a toggle's title, a list item's first line
  const inner = (content: DocNode[], head: DocNode | undefined, rebuild: (c: DocNode[]) => DocNode, whole: boolean) => {
    if (whole || names(head)) return null;
    const r = editBody({ type: "doc", content }, blocks, mode, anchor, ctx);
    // words that span its title and a line, or more than it holds, name the whole; a refusal in there stands
    if ("error" in r) return units(content).some(names) ? r : null;
    return { node: rebuild(r.body.content), changed: r.changed };
  };
  if (unit.type === "details") {
    const summary = unit.content?.find((c) => c.type === "detailsSummary");
    const inside = unit.content?.find((c) => c.type === "detailsContent");
    if (!inside) return null;
    return inner(inside.content ?? [], summary, (c) => ({ ...unit, content: (unit.content ?? []).map((x) => (x === inside ? { ...inside, content: c } : x)) }), blocks.some((b) => b.type === "details"));
  }
  const item = LISTS.has(unit.type) && unit.content?.length === 1 ? unit.content[0] : null;
  if (item && (item.content?.length ?? 0) > 1) {
    const own = squash(plainText(item.content![0]));
    return inner(item.content!.slice(1), item.content![0], (c) => ({ ...unit, content: [{ ...item, content: [item.content![0], ...c] }] }), !!own && squash(blocks.map(plainText).join(" ")).includes(own));
  }
  if (unit.type === "table" && mode !== "replace_all") {
    // the lines written, when every one is a row: Markdown reads a row with no header above it as words
    const lines = blocks.every((b) => b.type === "paragraph") ? blocks.flatMap((b) => inlineMarkdown(b.content, ctx).split("\n")).map((l) => l.trim()).filter(Boolean) : [];
    if (!lines.length || !lines.every((l) => l.startsWith("|"))) return null;
    const rows = unit.content ?? [];
    const rowText = rows.map((r) => [squash(plainText(r)), squash(tableMd({ type: "table", content: [r] }, ctx).split("\n")[0])]);
    const hit = (q: string) => rowText.flatMap(([t, m], i) => (t.includes(q) || m.includes(q) ? [i] : []));
    const [a, b] = parts.length > 1 ? [hit(parts[0]), hit(parts[parts.length - 1])] : [hit(parts[0] ?? ""), hit(parts[0] ?? "")];
    // rows written mean rows: words that name no one row, or two, change nothing and say so, never the whole table
    // (review: "Pro" in Pro and Pro Plus replaced the table with one line)
    if (a.length !== 1 || b.length !== 1 || b[0] < a[0]) {
      const n = a.length !== 1 ? a.length : b.length;
      return { error: n > 1 ? `"${anchor}" is in ${n} rows of the table. Use more of the row's words, so it names one.` : `No one row of the table has the words "${anchor}". Name the row with words from it.` };
    }
    // the new rows read under this table's header, so their cells come out as its rows' do
    const head = tableMd({ type: "table", content: [rows[0]] }, ctx);
    const made = fromMarkdown(`${head}\n${lines.join("\n")}`).blocks.find((x) => x.type === "table")?.content?.slice(1) ?? [];
    if (!made.length) return { error: `Those lines did not read as rows of the table. Write each as | a | b |, with a cell for each column.` };
    // rows written in place of the header: the first is the header now
    if (mode === "replace" && a[0] === 0) made[0] = { ...made[0], content: (made[0].content ?? []).map((c) => ({ ...c, type: "tableHeader" })) };
    const content = mode === "insert_after" ? [...rows.slice(0, b[0] + 1), ...made, ...rows.slice(b[0] + 1)] : [...rows.slice(0, a[0]), ...made, ...rows.slice(b[0] + 1)];
    return { node: { ...unit, content }, changed: [String(unit.attrs?.id ?? "")] };
  }
  return null;
}

/**
 * A page's body with new blocks put where an agent asked: at the end, in place of everything, after an
 * anchor, or in place of the blocks at an anchor. A block an agent wrote back word for word keeps the
 * block it was (its id, and anything Markdown does not carry), so only what changed is new.
 */
export function editBody(body: unknown, blocks: DocNode[], mode: WriteMode, anchor: string | undefined, ctx: ToMdContext = {}): EditResult | { error: string } {
  const before = asBody(body).content;
  let list = units(before);
  const fresh = units(blocks);
  let at: AnchorMatch | null = null;
  if (mode === "insert_after" || mode === "replace") {
    if (!anchor || !anchor.trim()) return { error: `${mode} needs an anchor: a short exact text from one block, or "start...end" for a run of blocks.` };
    const found = findAnchor(before, anchor, ctx);
    if ("error" in found) return found;
    at = found;
  }
  // inside a toggle, a list item's own list or a table: the edit is made there
  if (at && at.from === at.to) {
    const done = editInside(list[at.from], blocks, mode, anchor!, ctx);
    if (done && "error" in done) return done;
    if (done) {
      list = [...list.slice(0, at.from), done.node, ...list.slice(at.from + 1)];
      const result = joinLists(list);
      const left = new Set(widgetIds({ type: "doc", content: result }));
      return { body: { type: "doc", content: result }, changed: done.changed.filter(Boolean), removedWidgets: widgetIds({ type: "doc", content: before }).filter((id) => !left.has(id)) };
    }
  }
  // the same words sent again to the end, as an agent that tried again after a slow reply does: they are there once
  if (mode === "append" && fresh.length && fresh.length <= list.length && fresh.every((n, i) => blockMarkdown(n, ctx) === blockMarkdown(list[list.length - fresh.length + i], ctx)))
    return { body: asBody(body), changed: [], removedWidgets: [], already: true };
  let removed: DocNode[] = [];
  let placed: DocNode[] = fresh;
  // a block written back word for word is the block it was
  const keep = (gone: DocNode[]) => {
    const pool = gone.map((g) => ({ node: g, md: blockMarkdown(g, ctx) }));
    placed = fresh.map((n) => {
      const md = blockMarkdown(n, ctx);
      const i = pool.findIndex((p) => p.md === md);
      if (i < 0) return n;
      const [hit] = pool.splice(i, 1);
      return hit.node;
    });
  };
  if (mode === "append") list = [...list, ...placed];
  else if (mode === "replace_all") {
    removed = list;
    keep(list);
    list = placed;
  } else if (mode === "insert_after") list = [...list.slice(0, at!.to + 1), ...placed, ...list.slice(at!.to + 1)];
  else {
    removed = list.slice(at!.from, at!.to + 1);
    // a list item is replaced whole, the lines nested under it too: a replace that brings none of them back
    // would take them all out, so it is refused (an agent aiming at one nested line took out its item, 20 Oct)
    const nested = removed.reduce((n, u) => n + nestedLines(u), 0);
    if (nested && !fresh.some((u) => nestedLines(u) > 0)) {
      const first = squash(plainText(removed.find((u) => nestedLines(u) > 0)!.content?.[0]?.content?.[0] ?? { type: "paragraph" })).slice(0, 60);
      return {
        error: `That would take out the ${nested === 1 ? "line" : `${nested} lines`} nested under "${first}", since replace swaps a whole list item. Pass the item with the lines nested under it, indented as read_page shows them, changing only what you mean to change. Nothing was changed.`,
      };
    }
    keep(removed);
    list = [...list.slice(0, at!.from), ...placed, ...list.slice(at!.to + 1)];
  }
  const changed = placed.filter((n) => !removed.includes(n)).map((n) => String(n.attrs?.id ?? ""));
  const result = joinLists(list);
  const left = new Set(widgetIds({ type: "doc", content: result }));
  const removedWidgets = widgetIds({ type: "doc", content: before }).filter((id) => !left.has(id));
  return { body: { type: "doc", content: result }, changed: changed.filter(Boolean), removedWidgets };
}
